import { deleteAccountDataUseCase } from '@/domain/usecases/user/deleteAccountDataUseCase';
import { signOutUseCase } from '@/domain/usecases/login/signOutUseCase';

/**
 * DeleteAccountUseCase
 *
 * Orchestrates the account-deletion flow: request the async account deletion
 * via the `deleteAccountData` Cloud Function, then sign the user out.
 *
 * Web parity for Flutter's `AppAuthenticationService.deleteAccountDataAndFirebaseUser()`
 * (lib/services/app_authentication_service.dart:402-429): CF-call first, then sign-out.
 * Flutter's client-side `user.delete()` is commented-out dead code (:407-413) and is
 * intentionally NOT ported — web does CF-call + sign-out only.
 *
 * The CF-first-then-sign-out order is load-bearing: `deleteAccountData` rejects an
 * unauthenticated caller (server-side auth gate — the CF guards on the current user,
 * mirrored by cloud_functions_service.dart:249), so signing out first would make the
 * delete request fail.
 *
 * Unlike Flutter's fire-and-forget swallow, the web intentionally awaits and fails
 * loud on a CF error: if the delete step throws it propagates and sign-out is never
 * reached, so the visitor keeps their session and can retry (the account was not
 * actually requested-for-deletion). This is appropriate for the compliance page —
 * deletion must actually be requested before the session is torn down.
 */
export class DeleteAccountUseCase {
    async execute(): Promise<void> {
        // Request the async account deletion first — must run while authenticated.
        await deleteAccountDataUseCase.execute();
        // Only tear down the session once the delete request succeeded.
        await signOutUseCase.execute();
    }
}

export const deleteAccountUseCase = new DeleteAccountUseCase();
